import type {
  PublicQuestionnaire,
  PublicVendorSearchResult,
  QuestionnaireAnswerValue,
} from '@peles/shared';

/**
 * Public (no-auth) questionnaire fill API. Uses plain fetch — the
 * authenticated apiClient would redirect to /login on 401 and attach
 * producer tokens, neither of which applies to the tokenized client link.
 */

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = 'Request failed';
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      message = body.error?.message ?? message;
    } catch {
      // keep default message
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export async function getPublicQuestionnaire(token: string): Promise<PublicQuestionnaire> {
  const res = await fetch(`/api/v1/questionnaires/public/${encodeURIComponent(token)}`);
  return handle<PublicQuestionnaire>(res);
}

export interface SavedPublicAnswer {
  questionId: string;
  value: QuestionnaireAnswerValue;
  vendorId: string | null;
  vendorBusinessName: string | null;
}

export async function savePublicAnswer(
  token: string,
  questionId: string,
  input: {
    value: QuestionnaireAnswerValue;
    vendorId?: string | null;
    clientTimezone?: string;
  },
): Promise<SavedPublicAnswer> {
  const res = await fetch(
    `/api/v1/questionnaires/public/${encodeURIComponent(token)}/answers/${encodeURIComponent(questionId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  );
  return handle<SavedPublicAnswer>(res);
}

export async function submitPublicQuestionnaire(
  token: string,
  clientTimezone?: string,
): Promise<PublicQuestionnaire> {
  const res = await fetch(`/api/v1/questionnaires/public/${encodeURIComponent(token)}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clientTimezone }),
  });
  return handle<PublicQuestionnaire>(res);
}

export async function searchPublicVendors(
  token: string,
  query: string,
): Promise<PublicVendorSearchResult[]> {
  const res = await fetch(
    `/api/v1/questionnaires/public/${encodeURIComponent(token)}/vendor-search?q=${encodeURIComponent(query)}`,
  );
  return handle<PublicVendorSearchResult[]>(res);
}
